export interface ActivityLogEntry {
  id: string;
  family_id: string;
  user_id: string;
  user_email?: string;
  user_display_email?: string;
  action: string;
  details: string;
  created_at: string;
  metadata?: Record<string, any>;
  entity_name?: string;
  entity_type?: string;
  entity_id?: string;
  changes?: Record<string, any>;
}

export type ActivityAction = 
  | 'calendar_entry_created'
  | 'calendar_entry_updated'
  | 'calendar_entry_deleted'
  | 'event_created'
  | 'event_updated'
  | 'event_deleted'
  | 'note_created'
  | 'note_updated'
  | 'note_deleted'
  | 'expense_created'
  | 'expense_updated'
  | 'expense_deleted'
  | 'member_added'
  | 'member_removed'
  | 'member_role_changed'
  | 'child_added'
  | 'child_updated'
  | 'child_removed'
  | 'family_settings_updated'
  | 'invitation_sent'
  | 'invitation_accepted'
  | 'invitation_rejected'
  | 'system_update';

export function formatActivityAction(action: string): string {
  const actionMap: Record<string, string> = {
    'calendar_entry_created': 'Dodano wpis w kalendarzu',
    'calendar_entry_updated': 'Zaktualizowano wpis w kalendarzu',
    'calendar_entry_deleted': 'Usunięto wpis z kalendarza',
    'event_created': 'Dodano wydarzenie',
    'event_updated': 'Zaktualizowano wydarzenie',
    'event_deleted': 'Usunięto wydarzenie',
    'note_created': 'Dodano notatkę',
    'note_updated': 'Zaktualizowano notatkę',
    'note_deleted': 'Usunięto notatkę',
    'expense_created': 'Dodano koszt',
    'expense_updated': 'Zaktualizowano koszt',
    'expense_deleted': 'Usunięto koszt',
    'member_added': 'Dodano członka rodziny',
    'member_removed': 'Usunięto członka rodziny',
    'member_role_changed': 'Zmieniono rolę członka rodziny',
    'child_added': 'Dodano dziecko',
    'child_updated': 'Zaktualizowano dane dziecka',
    'child_removed': 'Usunięto dziecko',
    'family_settings_updated': 'Zaktualizowano ustawienia rodziny',
    'invitation_sent': 'Wysłano zaproszenie',
    'invitation_accepted': 'Zaproszenie zostało przyjęte',
    'invitation_rejected': 'Zaproszenie zostało odrzucone',
    'system_update': 'Aktualizacja systemowa'
  };

  return actionMap[action] || action;
}

export function formatActivityDetails(entry: ActivityLogEntry): string {
  // Debug w DEV: sprawdź jakie dane mamy (bez PII w produkcji)
  if (import.meta && !import.meta.env?.PROD) {
    // Uwaga: nie logujemy całych obiektów w produkcji
  }
  
  if (!entry.metadata) {
    console.log('⚠️ Brak metadata, używam details:', entry.details);
    return entry.details;
  }

  switch (entry.action) {
    case 'calendar_entry_created':
      const dateFromEntity = extractDateFromEntityName(entry.entity_name);
      return `Dodano wpis dla dziecka na dzień ${formatDate(dateFromEntity)}`;
    
    case 'calendar_entry_updated':
      const dateFromEntityUpd = extractDateFromEntityName(entry.entity_name);
      return `Zmieniono wpis dla dziecka na dzień ${formatDate(dateFromEntityUpd)}`;
    
    case 'calendar_entry_deleted':
      const dateFromEntityDel = extractDateFromEntityName(entry.entity_name);
      return `Usunięto wpis dla dziecka z dnia ${formatDate(dateFromEntityDel)}`;
    
    case 'member_added':
      return `Dodano użytkownika ${entry.metadata.email || ''} jako ${formatRole(entry.metadata.role)}`;
    
    case 'member_removed':
      return `Usunięto użytkownika ${entry.metadata.email || ''} (${formatRole(entry.metadata.role)})`;
    
    case 'member_role_changed':
      return `Zmieniono rolę użytkownika ${entry.metadata.email || ''} z ${formatRole(entry.metadata.old_role)} na ${formatRole(entry.metadata.new_role)}`;
    
    case 'child_added':
      return `Dodano dziecko: ${entry.metadata.name || ''}`;
    
    case 'child_updated':
      return `Zaktualizowano dane dziecka: ${entry.metadata.name || ''}`;
    
    case 'child_removed':
      return `Usunięto dziecko: ${entry.metadata.name || ''}`;
    
    case 'family_settings_updated':
      const changes = [];
      if (entry.metadata.name_changed) changes.push('nazwa rodziny');
      if (entry.metadata.home_a_changed) changes.push('nazwa domu A');
      if (entry.metadata.home_b_changed) changes.push('nazwa domu B');
      return `Zmieniono ustawienia: ${changes.join(', ')}`;
    
    case 'invitation_sent':
      return `Wysłano zaproszenie do ${entry.metadata.email || ''} (rola: ${formatRole(entry.metadata.role)})`;
    
    case 'invitation_accepted':
      return `Zaproszenie dla ${entry.metadata.email || ''} zostało przyjęte`;
    
    case 'invitation_rejected':
      return `Zaproszenie dla ${entry.metadata.email || ''} zostało odrzucone`;
    
    case 'system_update':
      return `Aktualizacja systemu: ${entry.details}`;
    
    default:
      return entry.details;
  }
}

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  } catch (e) {
    return dateString;
  }
}

function extractDateFromEntityName(entityName?: string): string {
  if (!entityName) return '';
  
  // entity_name format: "2025-07-31 - day"
  const match = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function formatRole(role?: string): string {
  if (!role) return '';
  
  const roleMap: Record<string, string> = {
    'parent_a': 'Rodzic (administrator)',
    'parent_b': 'Rodzic',
    'caregiver': 'Opiekun',
    'child_viewer': 'Obserwator'
  };

  return roleMap[role] || role;
}